import { useEffect, useState } from 'react';
import { api, type PendingOrg } from '../api';
import { AppShell } from '../components/AppShell';

export function SuperAdminPage() {
  const [orgs, setOrgs] = useState<PendingOrg[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function load() {
    try {
      setErr(null);
      const data = await api.listPendingOrgs();
      setOrgs(data);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed_to_load');
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function approve(id: string) {
    setBusyId(id);
    try {
      await api.approveOrg(id);
      await load();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-brand/30 bg-brand/10 px-3 py-1 text-xs font-medium uppercase tracking-wider text-brand-light">
            Platform admin
          </div>
          <h1 className="mt-3 text-3xl font-bold tracking-tight">
            Pending approval
          </h1>
          <p className="mt-1 text-muted">
            New organizations awaiting review. Approving flips status to
            ACTIVE — members can then access the dashboard.
          </p>
        </div>

        {err && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {err}
          </div>
        )}

        {!orgs && !err && <div className="text-muted">Loading…</div>}

        {orgs && orgs.length === 0 && (
          <div className="card text-center text-muted">
            No pending organizations. 🎉
          </div>
        )}

        {orgs && orgs.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border">
            <table className="w-full divide-y divide-border text-sm">
              <thead className="bg-surface text-xs uppercase tracking-wider text-muted">
                <tr>
                  <th className="px-4 py-3 text-left">Organization</th>
                  <th className="px-4 py-3 text-left">Owner</th>
                  <th className="px-4 py-3 text-left">Created</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {orgs.map((o) => {
                  const owner = o.users[0];
                  return (
                    <tr key={o.id}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-text">{o.name}</div>
                        <div className="font-mono text-xs text-muted">
                          {o.slug}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {owner ? (
                          <>
                            <div className="text-text">{owner.name}</div>
                            <div className="text-xs text-muted">
                              {owner.email}
                            </div>
                          </>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted">
                        {new Date(o.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          className="btn btn-primary"
                          disabled={busyId === o.id}
                          onClick={() => approve(o.id)}
                        >
                          {busyId === o.id ? 'Approving…' : 'Approve'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
